import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  MenuItem,
  OpenTabOptions,
  TabCloseGuard,
  TabContextValue,
  TabState,
  ToolRef,
  ToolManifest,
} from '@/types';
import { track } from '@/lib/analytics';
import { buildInstanceKey, tabIdentity } from '@/lib/tab-instance-key';
import { NotificationContext } from './NotificationProvider';
import { manifestIndex } from '@/lib/__generated__/manifest-index';

export const TabContext = createContext<TabContextValue | null>(null);

const HOME_ITEM: MenuItem = {
  id: 'home',
  label: 'Home',
  icon: 'home',
  type: 'tool',
  componentKey: 'Home',
  order: 0,
};

let tabCounter = 0;

/**
 * Workspace-level open-tab cap (report-frontend-decisions.md J4) - "a hard
 * workspace-level cap of 12 open tabs, LRU-evicted the same way C2b's
 * `MAX_RESIDENT_SOURCES` already works, one level up". Before this, every
 * open tab stayed mounted with its decoded result sets resident and no
 * global cap - tolerable for one report's data, but not once FE-2's
 * columnar substrate means each tab can hold a meaningful share of J1's
 * provisional 100k-row ceiling. A 13th tab evicts the least-recently-
 * ACTIVATED closeable tab (never the tab currently open), not the
 * least-recently-created one - `lruOrderRef` tracks activation, not
 * `tabs`'s own (drag-reorderable) array order. Re-opening an evicted tab's
 * `componentKey` is indistinguishable from opening it fresh (`openTab`'s own
 * existing-tab check no longer finds it), so it re-executes - the same
 * idempotent-revisit behavior C2b already established for evicted result
 * sets, not an error state.
 */
const MAX_OPEN_TABS = 12;

export function TabProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<TabState[]>(() => {
    const homeTab = createTabState(HOME_ITEM, true, {});
    return [homeTab];
  });
  const [activeTabId, setActiveTabId] = useState<string>('home');
  // Mirror of `tabs` so `openTab` can decide (evict / refuse / notify)
  // outside a state updater. Also written by `openTab` itself so several
  // opens inside one batch see each other.
  const tabsRef = useRef<TabState[]>(tabs);
  tabsRef.current = tabs;
  // Per-tab dirty flag, fed by `useTabDirty()` (ADR-18). Drives eviction only.
  const [dirtyIdsMap, setDirtyIdsMap] = useState<Record<string, boolean>>({});
  // Null-safe: TabProvider may mount without a NotificationProvider (tests).
  const notificationContext = useContext(NotificationContext);
  // Per-tab row selection. Lives here (not inside each tool) so context
  // identity changes on selection — drives ribbon re-render via
  // `useCommandResolution`. Tools consume through `useToolSelection`.
  const [selectedIdsMap, setSelectedIdsMap] = useState<Record<string, string[]>>({});

  // J4's LRU order - most-recently-ACTIVATED tab id is last. A ref, not
  // state: it's write-through eviction bookkeeping, never itself rendered,
  // the same shape `useReportExecutionDirect.ts`'s `visitedRef` already uses
  // one level down for `MAX_RESIDENT_SOURCES`.
  const lruOrderRef = useRef<string[]>(['home']);
  const touchLru = useCallback((tabId: string) => {
    lruOrderRef.current = [...lruOrderRef.current.filter((id) => id !== tabId), tabId];
  }, []);
  const dropFromLru = useCallback((tabId: string) => {
    lruOrderRef.current = lruOrderRef.current.filter((id) => id !== tabId);
  }, []);

  const setTabSelectedIds = useCallback((tabId: string, ids: string[]) => {
    setSelectedIdsMap((prev) => {
      const current = prev[tabId];
      if (current && current.length === ids.length && current.every((v, i) => v === ids[i])) {
        return prev;
      }
      return { ...prev, [tabId]: ids };
    });
  }, []);

  const setTabDirty = useCallback((tabId: string, dirty: boolean) => {
    setDirtyIdsMap((prev) => {
      if (Boolean(prev[tabId]) === dirty) return prev;
      if (!dirty) {
        const next = { ...prev };
        delete next[tabId];
        return next;
      }
      return { ...prev, [tabId]: true };
    });
  }, []);

  const clearDirty = useCallback((tabIds: string[]) => {
    setDirtyIdsMap((prev) => {
      if (!tabIds.some((id) => id in prev)) return prev;
      const next = { ...prev };
      for (const id of tabIds) delete next[id];
      return next;
    });
  }, []);

  /**
   * Open (or activate) a tab for `item`.
   *
   * Identity is `item.componentKey` plus, when supplied, the instance key -
   * `item.id` and `item.order` play no part. With no `options` this is the
   * original one-tab-per-tool behavior.
   *
   * I7 contract (report-frontend-architecture drill-through): pass
   * `{ params, label }` where `params` is a flat record of JSON primitives
   * mapped from the source row's column values. The instance key is derived
   * from `params` (key-order independent) unless `instanceKey` is given, so
   * the same tool + same params activates the existing tab. The tool receives
   * `instanceKey` and `params` as props. A re-activated tab keeps its first
   * params and label.
   *
   * When the cap is reached and every non-active closeable tab is dirty, the
   * open is refused with a warning notification (ADR-4).
   */
  const openTab = useCallback(
    (item: MenuItem, options?: OpenTabOptions) => {
      const prev = tabsRef.current;
      const instanceKey = options?.instanceKey ?? buildInstanceKey(options?.params);
      const identity = tabIdentity(item.componentKey, instanceKey);

      // If a tab with this identity already exists, activate it
      const existing = prev.find((t) => tabIdentity(t.componentKey, t.instanceKey) === identity);
      if (existing) {
        setActiveTabId(existing.id);
        touchLru(existing.id);
        return;
      }

      let next = prev;
      // J4: evict the least-recently-activated CLOSEABLE, non-dirty tab before
      // a 13th opens - never the tab currently active. A dirty tab holds
      // unsaved work, so it is never a victim (ADR-4/ADR-18).
      if (next.length >= MAX_OPEN_TABS) {
        const victimId = lruOrderRef.current.find(
          (id) =>
            id !== activeTabId && !dirtyIdsMap[id] && next.some((t) => t.id === id && t.isCloseable),
        );
        if (victimId) {
          next = next.filter((t) => t.id !== victimId);
          dropFromLru(victimId);
          clearDirty([victimId]);
          setSelectedIdsMap((prevSel) => {
            if (!(victimId in prevSel)) return prevSel;
            const nextSel = { ...prevSel };
            delete nextSel[victimId];
            return nextSel;
          });
        } else if (
          next.some((t) => t.id !== activeTabId && t.isCloseable && dirtyIdsMap[t.id])
        ) {
          notificationContext?.addNotification({
            type: 'warning',
            title: 'Too many open tabs',
            message: 'Close or save a tab to open another.',
          });
          return;
        }
      }

      const newTab = createTabState(item, true, { ...options, instanceKey });
      const updated = [...next, newTab];
      tabsRef.current = updated;
      setTabs(updated);
      setActiveTabId(newTab.id);
      touchLru(newTab.id);
      if (item.type === 'report' && item.reportKey) {
        track('report_opened', {
          report_key: item.reportKey,
          layout_type: item.layoutType ?? 'grid',
          entry: options?.entry ?? 'menu',
        });
      }
    },
    [activeTabId, dirtyIdsMap, touchLru, dropFromLru, clearDirty, notificationContext],
  );

  // Opt-in close guards (see `TabContextValue.registerCloseGuard`). Empty by default.
  const closeGuardsRef = useRef<Map<string, TabCloseGuard>>(new Map());
  const registerCloseGuard = useCallback((tabId: string, guard: TabCloseGuard) => {
    closeGuardsRef.current.set(tabId, guard);
    return () => {
      if (closeGuardsRef.current.get(tabId) === guard) closeGuardsRef.current.delete(tabId);
    };
  }, []);

  // Read through a ref so `forceCloseTab` stays referentially stable: it is part of every tab's
  // `TabInstanceContext` value, which must not change identity when the active tab does.
  const activeTabIdRef = useRef(activeTabId);
  activeTabIdRef.current = activeTabId;

  const forceCloseTab = useCallback(
    (tabId: string) => {
      setTabs((prev) => {
        const tab = prev.find((t) => t.id === tabId);
        if (!tab || !tab.isCloseable) return prev;

        const filtered = prev.filter((t) => t.id !== tabId);
        dropFromLru(tabId);

        // If we closed the active tab, activate the nearest one
        if (tabId === activeTabIdRef.current && filtered.length > 0) {
          const closedIndex = prev.findIndex((t) => t.id === tabId);
          const newActiveIndex = Math.min(closedIndex, filtered.length - 1);
          setActiveTabId(filtered[newActiveIndex]!.id);
          touchLru(filtered[newActiveIndex]!.id);
        }

        return filtered;
      });
      clearDirty([tabId]);
      // Drop the closed tab's selection slice so re-opening the same
      // componentKey starts fresh (matches the previous local-state behavior).
      setSelectedIdsMap((prev) => {
        if (!(tabId in prev)) return prev;
        const next = { ...prev };
        delete next[tabId];
        return next;
      });
    },
    [dropFromLru, touchLru, clearDirty],
  );

  const closeTab = useCallback(
    (tabId: string) => {
      const guard = closeGuardsRef.current.get(tabId);
      if (guard?.()) {
        setActiveTabId(tabId);
        touchLru(tabId);
        return;
      }
      forceCloseTab(tabId);
    },
    [forceCloseTab, touchLru],
  );

  const activateTab = useCallback(
    (tabId: string) => {
      setActiveTabId(tabId);
      touchLru(tabId);
    },
    [touchLru],
  );

  const reorderTabs = useCallback((fromIndex: number, toIndex: number) => {
    setTabs((prev) => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      if (removed) {
        result.splice(toIndex, 0, removed);
      }
      return result;
    });
  }, []);

  // A tab that registered a close guard and holds unsaved work survives a bulk close.
  const guardedDirtyIds = useCallback(
    (candidates: TabState[]) => candidates.filter((t) => closeGuardsRef.current.has(t.id) && dirtyIdsMap[t.id]).map((t) => t.id),
    [dirtyIdsMap],
  );

  const closeAllTabs = useCallback(() => {
    const kept = guardedDirtyIds(tabsRef.current);
    if (kept.length > 0) {
      setTabs((prev) => prev.filter((t) => kept.includes(t.id)));
      setDirtyIdsMap((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => kept.includes(id))));
      setActiveTabId(kept[0]!);
      lruOrderRef.current = kept;
      return;
    }
    setTabs([]);
    setDirtyIdsMap({});
    setActiveTabId('');
    lruOrderRef.current = [];
  }, [guardedDirtyIds]);

  const closeOtherTabs = useCallback(
    (tabId: string) => {
      const kept = [tabId, ...guardedDirtyIds(tabsRef.current.filter((t) => t.id !== tabId))];
      setTabs((prev) => prev.filter((t) => kept.includes(t.id)));
      setDirtyIdsMap((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => kept.includes(id))));
      setActiveTabId(tabId);
      lruOrderRef.current = kept;
    },
    [guardedDirtyIds],
  );

  const activeTab = useMemo(() => tabs.find((t) => t.id === activeTabId) ?? null, [tabs, activeTabId]);

  const value = useMemo<TabContextValue>(
    () => ({
      tabs,
      activeTabId,
      activeToolRef: activeTab?.ref ?? null,
      activeManifest: activeTab?.manifest ?? null,
      selectedIdsMap,
      setTabSelectedIds,
      dirtyIdsMap,
      setTabDirty,
      openTab,
      closeTab,
      activateTab,
      reorderTabs,
      closeAllTabs,
      closeOtherTabs,
      registerCloseGuard,
      forceCloseTab,
    }),
    [
      tabs,
      activeTabId,
      activeTab,
      selectedIdsMap,
      setTabSelectedIds,
      dirtyIdsMap,
      setTabDirty,
      openTab,
      closeTab,
      activateTab,
      reorderTabs,
      closeAllTabs,
      closeOtherTabs,
      registerCloseGuard,
      forceCloseTab,
    ],
  );

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
}

function createTabState(
  item: MenuItem,
  isCloseable: boolean,
  options: OpenTabOptions,
): TabState {
  const id = item.id === 'home' ? 'home' : `tab-${++tabCounter}`;
  const manifest: ToolManifest | null = manifestIndex[item.componentKey] ?? null;

  return {
    id,
    componentKey: item.componentKey,
    label: options.label ?? item.label,
    icon: item.icon,
    isCloseable,
    order: tabCounter,
    ref: { current: null } as React.RefObject<ToolRef | null>,
    manifest,
    toolConfig: item,
    instanceKey: options.instanceKey,
    params: options.params,
  };
}
